import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, UserPlus, X, Search, Loader2, Trash2, AlertCircle, Globe,
  Mail, RefreshCw, CheckCircle2,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes to backend on the serving domain.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function AddFitterModal({ onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/email/weekly-fitter/fitters`, {
        email: email.trim(), name: name.trim(), country: country.trim(), notes: notes.trim(),
      });
      onAdded(email.trim());
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add fitter');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-hg-surface border border-hg-border rounded-sm w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-fitter-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-hg-text tracking-wider">ADD FITTER</h3>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-hg-text-dim font-sans -mt-2">
          Adds (or updates) this person and assigns them to the Weekly Fitter — All Fitters list. No other list is touched.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Email</label>
            <input data-testid="add-fitter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Name</label>
            <input data-testid="add-fitter-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="First Last"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Country</label>
            <input data-testid="add-fitter-country" value={country} onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. South Africa"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1.5">Notes (optional)</label>
            <textarea data-testid="add-fitter-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} placeholder="Anything worth remembering…"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none" />
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-2 flex items-start gap-2">
              <AlertCircle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-[11px] text-red-400 font-sans">{error}</span>
            </div>
          )}
          <button data-testid="add-fitter-submit" onClick={handleSubmit} disabled={submitting}
            className="w-full bg-hg-green text-hg-text text-sm font-sans font-medium py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {submitting ? 'Adding…' : 'Add Fitter'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FittersList() {
  const [fitters, setFitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState('');
  const [notice, setNotice] = useState('');
  const [targetName, setTargetName] = useState('Weekly Fitter — All Fitters');
  const [targetCount, setTargetCount] = useState(null);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

  const loadTarget = useCallback(async () => {
    try {
      const t = await axios.get(`${API}/email/weekly-fitter/target-info`);
      if (t.data?.group_name) setTargetName(t.data.group_name);
      if (t.data?.subscriber_count != null) setTargetCount(t.data.subscriber_count);
    } catch { /* display-only */ }
  }, []);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '50');
      const res = await axios.get(`${API}/email/weekly-fitter/fitters?${params.toString()}`);
      setFitters(res.data.fitters || []);
      setNextCursor(res.data.next_cursor || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load fitters');
    }
    setLoading(false);
    setRefreshing(false);
  }, [search]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('limit', '50');
      params.set('cursor', nextCursor);
      const res = await axios.get(`${API}/email/weekly-fitter/fitters?${params.toString()}`);
      setFitters((curr) => [...curr, ...(res.data.fitters || [])]);
      setNextCursor(res.data.next_cursor || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load more');
    }
    setLoadingMore(false);
  };

  useEffect(() => { loadTarget(); }, [loadTarget]);
  useEffect(() => { load(); }, [load]);

  const handleRemove = async (f) => {
    if (!window.confirm(
      `Remove ${f.email} from "${targetName}"?\n\nThis only removes them from this list — they remain a subscriber and stay in any other groups.`
    )) return;
    setActionId(f.id);
    try {
      await axios.delete(`${API}/email/weekly-fitter/fitters/${f.id}`);
      setFitters((curr) => curr.filter((x) => x.id !== f.id));
      flash(`Removed ${f.email} from the list.`);
      loadTarget();
    } catch (e) {
      setError(e.response?.data?.detail || 'Remove failed');
    }
    setActionId('');
  };

  const handleAdded = (email) => {
    flash(`Added ${email}. It can take MailerLite a moment to index — hit Refresh if it isn't shown yet.`);
    load(false);
    loadTarget();
  };

  return (
    <div className="space-y-5" data-testid="fitters-page">
      {showAdd && <AddFitterModal onClose={() => setShowAdd(false)} onAdded={handleAdded} />}

      {notice && (
        <div className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 shadow-lg max-w-sm">
          <CheckCircle2 size={14} className="flex-shrink-0" /> {notice}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-hg-text tracking-wider flex items-center gap-2">
            <Globe size={18} className="text-hg-green" /> Manage Fitters
          </h2>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            The Weekly Fitter audience: <strong className="text-hg-text">{targetName}</strong>
            {targetCount != null && <span> · ~{targetCount} recipients</span>}. Add international fitters and manage the combined list here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="fitters-refresh-btn"
            onClick={() => { setRefreshing(true); load(false); loadTarget(); }} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button data-testid="fitters-add-btn" onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors">
            <UserPlus size={12} /> Add Fitter
          </button>
        </div>
      </div>

      {/* search */}
      <div className="relative">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
        <input data-testid="fitters-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email or name…"
          className="w-full lg:w-80 bg-hg-bg border border-hg-border rounded-sm pl-8 pr-3 py-2 text-xs text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
      </div>

      {/* list */}
      <div className="bg-hg-surface border border-hg-border rounded-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-hg-border">
          <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">{targetName}</h3>
          <span className="text-[10px] text-hg-text-dim font-sans">{fitters.length} loaded</span>
        </div>

        {error && (
          <div className="m-5 bg-red-500/10 border border-red-500/30 rounded-sm p-3 flex items-start gap-2">
            <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-red-400 font-sans">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
        ) : fitters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Users size={24} className="text-hg-text-dim" strokeWidth={1.5} />
            <p className="text-sm text-hg-text-dim font-sans">No fitters in this view.</p>
          </div>
        ) : (
          <div data-testid="fitters-rows">
            {fitters.map((f) => (
              <div key={f.id} data-testid={`fitter-row-${f.id}`} className="border-b border-hg-border/40 last:border-b-0 px-5 py-3 flex items-center justify-between gap-3 hover:bg-hg-bg/40">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Mail size={12} className="text-hg-text-dim flex-shrink-0" />
                    <span className="text-sm font-sans text-hg-text truncate">{f.email}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                      f.status === 'active' ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border/40 text-hg-text-dim'
                    }`}>{f.status}</span>
                    {f.country && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-hg-border/40 text-hg-text-dim flex items-center gap-1">
                        <Globe size={8} /> {f.country}
                      </span>
                    )}
                  </div>
                  {f.name && <div className="text-[11px] text-hg-text-dim font-sans mt-0.5 ml-5">{f.name}</div>}
                  {f.notes && <div className="text-[10px] text-hg-text-dim/80 font-sans mt-0.5 ml-5 italic">{f.notes}</div>}
                </div>
                <button data-testid={`fitter-remove-${f.id}`} onClick={() => handleRemove(f)} disabled={actionId === f.id}
                  title="Remove from this list (keeps the subscriber)"
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-sans text-hg-text-dim hover:text-red-400 transition-colors disabled:opacity-40">
                  {actionId === f.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                </button>
              </div>
            ))}
          </div>
        )}

        {nextCursor && !loading && (
          <div className="px-5 py-3 border-t border-hg-border flex justify-center">
            <button onClick={loadMore} disabled={loadingMore}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40">
              {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null} Load more
            </button>
          </div>
        )}
      </div>

      <p className="text-[10px] text-hg-text-dim font-sans flex items-center gap-1">
        <AlertCircle size={11} /> Removing a fitter only takes them off this list — it never deletes the subscriber or touches the "2026 Fitter List USA" or any other group.
      </p>
    </div>
  );
}
